import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [welcomeAdded, setWelcomeAdded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const { customer, isAuthenticated } = useCustomerAuth();

  // Add message helper function - wrapped in useCallback to prevent re-creation
  const addMessage = useCallback((sender, text, extraData = {}) => {
    const newMessage = {
      id: Date.now() + Math.random(),
      text,
      sender,
      timestamp: new Date(),
      ...extraData
    };
    setMessages(prev => [...prev, newMessage]);
  }, []);

  // Initialize with personalized welcome message - only once
  useEffect(() => {
    if (!welcomeAdded && customer && isAuthenticated) {
      const customerName = customer.firstName || 'Valued Customer';
      addMessage('bot', `Hello ${customerName}! I'm Reva, your personal shopping assistant. I can help you with your orders, track your purchases, find products you've bought, and assist with returns from the stores you shop from. How can I help you today?`);
      setWelcomeAdded(true);
    }
  }, [welcomeAdded, customer, isAuthenticated, addMessage]);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Debug isOpen state changes
  useEffect(() => {
    console.log('isOpen state changed to:', isOpen);
  }, [isOpen]);

  // Focus input when chatbot opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Listen for custom event to open chatbot with pre-filled query
  useEffect(() => {
    const handleOpenWithQuery = (event) => {
      const { query } = event.detail;
      setInputValue(query);
      setIsOpen(true);
    };

    window.addEventListener('openChatbotWithQuery', handleOpenWithQuery);

    return () => {
      window.removeEventListener('openChatbotWithQuery', handleOpenWithQuery);
    };
  }, []);

  // Debug authentication state
  console.log('Chatbot render check:', { isAuthenticated, customer: !!customer, customerId: customer?.id });
  
  // Don't render if customer is not authenticated
  if (!isAuthenticated || !customer) {
    console.log('Chatbot not rendering - authentication failed');
    return null;
  }

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Get customer's order history to understand their stores
      const ordersResponse = await fetch('/api/customer/order-tracking', {
        method: 'GET',
        credentials: 'include'
      });

      let customerStores = [];
      if (ordersResponse.ok) {
        const ordersData = await ordersResponse.json();
        customerStores = ordersData.orders?.map(order => ({
          storeName: order.storeName,
          storeId: order.storeId,
          lastOrderDate: order.createdAt
        })) || [];
      }

      const response = await fetch('/api/ai/customer/chatbot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          query: inputValue.trim(),
          customerId: customer?.id,
          sessionId: `session_${customer?.id}_${Date.now()}`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();

      const botMessage = {
        id: Date.now() + 1,
        text: data.response || data.data?.response || "I'm here to help!",
        sender: 'bot',
        timestamp: new Date(),
        intent: data.intent
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "I'm sorry, I'm having trouble connecting right now. Please try again later or contact our support team.",
        sender: 'bot',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        id: 1,
        text: "Hello! I'm Reva, your intelligent assistant. How can I help you today?",
        sender: 'bot',
        timestamp: new Date()
      }
    ]);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600';
    if (confidence >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getConfidenceText = (confidence) => {
    if (confidence >= 80) return 'High confidence';
    if (confidence >= 60) return 'Medium confidence';
    return 'Low confidence';
  };

  return (
    <>
      <style>
        {`
          @keyframes bounce {
            0%, 80%, 100% {
              transform: scale(0);
            }
            40% {
              transform: scale(1);
            }
          }
        `}
      </style>
      <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999 }}>
        {/* Chatbot Toggle Button */}
        <button
          onClick={() => {
            console.log('Chatbot button clicked, current state:', { isOpen, isAuthenticated, customer: !!customer });
            setIsOpen(prevIsOpen => {
              console.log('Toggling isOpen from', prevIsOpen, 'to', !prevIsOpen);
              return !prevIsOpen;
            });
          }}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            padding: '12px 20px',
            borderRadius: '25px',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.3s ease',
            minWidth: '140px',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.3)';
          }}
          aria-label="Open AI Assistant"
        >
          {isOpen ? (
            <>
              <svg style={{ width: '18px', height: '18px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Close</span>
            </>
          ) : (
            <>
              <span>✨ Reva Assistant</span>
              <svg style={{ width: '18px', height: '18px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </>
          )}
        </button>

        {/* Notification dot for new messages */}
        {!isOpen && messages.length > 1 && (
          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {messages.length - 1}
          </div>
        )}

        {/* Pulsing indicator when chatbot is available */}
        {!isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              width: '12px',
              height: '12px',
              backgroundColor: '#10B981',
              borderRadius: '50%',
              animation: 'pulse 2s infinite',
              zIndex: 1
            }}
          />
        )}

      {/* Chatbot Window */}
      {isOpen && (
        <div 
          style={{ 
            position: 'fixed',
            bottom: '100px',
            right: '24px',
            width: '384px',
            height: '500px',
            zIndex: 9998,
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            background: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
          {/* Header */}
          <div 
            style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '16px 20px',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: '60px'
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
              <div 
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                <svg 
                  style={{ width: '18px', height: '18px', color: '#ffffff' }} 
                  fill="currentColor" 
                  viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <h3 
                  style={{ 
                    fontSize: '16px', 
                    margin: 0, 
                    lineHeight: '1.2',
                    color: '#ffffff',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                  ✨ Reva Assistant
                </h3>
                <p 
                  style={{ 
                    fontSize: '11px', 
                    margin: 0, 
                    opacity: 0.8,
                    marginTop: '2px',
                    color: '#ffffff',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                  Powered by Revstar
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <button
                  onClick={clearChat}
                  style={{
                    padding: '6px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#ffffff',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                  }}
                  title="Clear chat"
                >
                  <svg 
                    style={{ width: '14px', height: '14px', color: '#ffffff' }} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  style={{
                    padding: '6px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#ffffff',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                  }}
                >
                  <svg 
                    style={{ width: '14px', height: '14px', color: '#ffffff' }} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div 
            style={{ 
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              background: '#fafafa',
              minHeight: 0,
              maxHeight: '300px'
            }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minHeight: '100%' }}>
              {messages.map((message) => (
                <div
                  key={message.id}
                  style={{
                    display: 'flex',
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <div
                    style={{
                      maxWidth: '280px',
                      padding: '12px 16px',
                      borderRadius: message.sender === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                      background: message.sender === 'user' 
                        ? '#3b82f6' 
                        : message.isError
                        ? '#fef2f2'
                        : '#ffffff',
                      color: message.sender === 'user' 
                        ? '#ffffff' 
                        : message.isError
                        ? '#dc2626'
                        : '#374151',
                      border: message.isError ? '1px solid #fecaca' : 'none',
                      boxShadow: message.sender === 'user' 
                        ? '0 2px 8px rgba(59, 130, 246, 0.3)' 
                        : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}
                  >
                    {message.text}
                    
                    {/* Bot message metadata */}
                    {message.sender === 'bot' && !message.isError && (
                      <div style={{ marginTop: '8px', fontSize: '11px', opacity: 0.7 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span>{formatTime(message.timestamp)}</span>
                          {message.confidence && (
                            <span style={{ fontWeight: '500' }}>
                              {getConfidenceText(message.confidence)}
                            </span>
                          )}
                        </div>
                        
                        {/* Suggested Actions */}
                        {message.suggestedActions && message.suggestedActions.length > 0 && (
                          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {message.suggestedActions.map((action, index) => (
                              <button
                                key={index}
                                onClick={() => setInputValue(action)}
                                style={{
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '6px 12px',
                                  background: 'rgba(255, 255, 255, 0.2)',
                                  borderRadius: '8px',
                                  fontSize: '11px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  color: 'inherit'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                                  e.target.style.transform = 'scale(1.02)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                💡 {action}
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* Human support indicator */}
                        {message.requiresHumanSupport && (
                          <div style={{ marginTop: '8px', color: '#f59e0b', fontWeight: '500', fontSize: '11px' }}>
                            ⚠️ May need human support
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* User message timestamp */}
                    {message.sender === 'user' && (
                      <div style={{ marginTop: '4px', fontSize: '11px', opacity: 0.7 }}>
                        {formatTime(message.timestamp)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Loading indicator */}
              {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{
                    background: '#f9fafb',
                    color: '#6b7280',
                    padding: '12px 16px',
                    borderRadius: '20px 20px 20px 4px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <div style={{ width: '8px', height: '8px', background: '#3b82f6', borderRadius: '50%', animation: 'bounce 1.4s ease-in-out infinite both' }}></div>
                        <div style={{ width: '8px', height: '8px', background: '#3b82f6', borderRadius: '50%', animation: 'bounce 1.4s ease-in-out infinite both', animationDelay: '0.1s' }}></div>
                        <div style={{ width: '8px', height: '8px', background: '#3b82f6', borderRadius: '50%', animation: 'bounce 1.4s ease-in-out infinite both', animationDelay: '0.2s' }}></div>
                      </div>
                      <span style={{ fontSize: '14px' }}>Reva is thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          <div 
            style={{
              borderTop: '1px solid #e5e7eb',
              padding: '20px',
              background: '#ffffff',
              borderBottomLeftRadius: '20px',
              borderBottomRightRadius: '20px',
              flexShrink: 0,
              height: '120px'
            }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                disabled={isLoading}
                style={{
                  flex: 1,
                  border: '2px solid #e5e7eb',
                  borderRadius: '25px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  outline: 'none',
                  background: '#ffffff',
                  transition: 'all 0.2s ease',
                  opacity: isLoading ? 0.5 : 1
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  border: 'none',
                  background: inputValue.trim() && !isLoading ? '#3b82f6' : '#d1d5db',
                  color: '#ffffff',
                  cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  opacity: inputValue.trim() && !isLoading ? 1 : 0.5
                }}
                onMouseEnter={(e) => {
                  if (inputValue.trim() && !isLoading) {
                    e.target.style.background = '#2563eb';
                    e.target.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (inputValue.trim() && !isLoading) {
                    e.target.style.background = '#3b82f6';
                    e.target.style.transform = 'scale(1)';
                  }
                }}
              >
                {isLoading ? (
                  <svg 
                    className="animate-spin" 
                    style={{ width: '20px', height: '20px' }} 
                    fill="none" 
                    viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg 
                    style={{ width: '20px', height: '20px' }} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </div>
            
            {/* Quick suggestions */}
            <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {(messages.length === 1 ? [
                "Show me my recent orders",
                "What stores have I shopped from?",
                "Track my orders"
              ] : [
                "Tell me more",
                "How do I return items?",
                "Show my order history"
              ]).map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => setInputValue(suggestion)}
                  style={{
                    fontSize: '12px',
                    background: '#ffffff',
                    color: '#6b7280',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#eff6ff';
                    e.target.style.color = '#3b82f6';
                    e.target.style.borderColor = '#bfdbfe';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#ffffff';
                    e.target.style.color = '#6b7280';
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default Chatbot;
